// app/components/thailand-premium-tapioca-starch-products/PoonPhol.tsx
"use client";
import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { renderWithLineBreaks } from "@/app/utils/renderWithLineBreaks";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function AtPoonPhol() {
  const t = useTranslations("OurProducts");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // path สำหรับเก็บใน CMS (ตัด /en /zh ด้านหน้าออก)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ---- default text จาก i18n (ดีต่อ SEO/SSR) ----
  const defaultTitle = t("PoonPhol.title");
  const defaultContent = t("PoonPhol.content");

  // ---- default images (เดิมเป็น static) ----
  const defaultImg1 = "/assets/thailand-premium-tapioca-starch-products/poonphol/img1.png";
  const defaultImg2 = "/assets/thailand-premium-tapioca-starch-products/poonphol/img2.png";
  const defaultImg3 = "/assets/thailand-premium-tapioca-starch-products/poonphol/img3.png";

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = React.useState(defaultTitle);
  const [content, setContent] = React.useState(defaultContent);

  const [img1, setImg1] = React.useState(defaultImg1);
  const [img2, setImg2] = React.useState(defaultImg2);
  const [img3, setImg3] = React.useState(defaultImg3);

  // locale เปลี่ยน → reset ค่า default ก่อน
  React.useEffect(() => {
    setTitle(defaultTitle);
    setContent(defaultContent);
    setImg1(defaultImg1);
    setImg2(defaultImg2);
    setImg3(defaultImg3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  //
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        // ---- apply override ตาม key ที่เรากำหนด ----
        if (map["ourproducts.poonphol.title"])
          setTitle(map["ourproducts.poonphol.title"]);
        if (map["ourproducts.poonphol.content"])
          setContent(map["ourproducts.poonphol.content"]);

        if (map["ourproducts.poonphol.img1"])
          setImg1(map["ourproducts.poonphol.img1"]);
        if (map["ourproducts.poonphol.img2"])
          setImg2(map["ourproducts.poonphol.img2"]);
        if (map["ourproducts.poonphol.img3"])
          setImg3(map["ourproducts.poonphol.img3"]);
      } catch (err) {
        console.warn("[OurProducts.PoonPhol] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <>
      <Box className="atpoonphol-wrap-bg" data-aos="fade-right"></Box>
      <Box className="atpoonphol-wrap">
        {/* Desktop Swiper */}
        <Box
          sx={{
            pt: { xs: 5, md: 15 },
            pb: { xs: 0, md: 5 },
            display: { xs: "none", md: "block" }
          }}>
          <Grid container>
            <Grid
              size={{ xs: 12, md: 5.9 }}
              offset={{ xs: 0, md: 6.1 }}
              data-aos="fade-left"
              sx={{
                pl: { xs: 3, md: 3 }
              }}
            >
              <Swiper
                spaceBetween={16}
                slidesPerView={1.2}
                loop
                autoplay={{ delay: 5000 }}
                modules={[Autoplay]}
              >
                <SwiperSlide>
                  <Image
                    src={img1}
                    title="Premium Tapioca Starch - PP/PE Bags 50 Kg"
                    alt="Premium Tapioca Starch - PP/PE Bags 50 Kg"
                    width={678}
                    height={442}
                    className="img-fluid"
                    data-cms-key="ourproducts.poonphol.img1"
                  />
                </SwiperSlide>
                <SwiperSlide>
                  <Image
                    src={img2}
                    title="Premium Tapioca Starch - PP/PE Bags 50 Kg"
                    alt="Premium Tapioca Starch - PP/PE Bags 50 Kg"
                    width={678}
                    height={442}
                    className="img-fluid"
                    data-cms-key="ourproducts.poonphol.img2"
                  />
                </SwiperSlide>
                <SwiperSlide>
                  <Image
                    src={img3}
                    title="Premium Tapioca Starch - PP/PE Bags 25 Kg"
                    alt="Premium Tapioca Starch - PP/PE Bags 25 Kg"
                    width={678}
                    height={442}
                    className="img-fluid"
                    data-cms-key="ourproducts.poonphol.img3"
                  />
                </SwiperSlide>
              </Swiper>
            </Grid>
          </Grid>
        </Box>

        {/* Content */}
        <Box className="atpoonphol-content">
          <Container
            className="container"
            sx={{ pt: { xs: 4, md: 0, lg: 5 } }}
          >
            <Grid container>
              <Grid
                size={{ xs: 12, md: 6, lg: 6.2 }}
                data-aos="fade-up"
                sx={{
                  pr: { xs: 0, md: 0 }
                }}
              >
                <Typography
                  variant="h2"
                  data-cms-key="ourproducts.poonphol.title"
                  sx={{
                    mb: 3,
                    whiteSpace: { xs: "normal", lg: "pre-line" }
                  }}>
                  {title}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{ textIndent: 2, whiteSpace: "pre-line" }}
                  data-cms-key="ourproducts.poonphol.content"
                >
                  {renderWithLineBreaks(content)}
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Mobile Swiper */}
        <Box
          sx={{
            pt: { xs: 3, md: 15 },
            pb: { xs: 0, md: 5 },
            display: { xs: "block", md: "none" }
          }}>
          <Grid container>
            <Grid
              size={{ xs: 12, md: 6 }}
              offset={{ xs: 0, md: 6 }}
              data-aos="fade-left"
              sx={{
                pl: { xs: 2, md: 3 }
              }}
            >
              <Swiper
                spaceBetween={16}
                slidesPerView={1.2}
                loop
                autoplay={{ delay: 5000 }}
                modules={[Autoplay]}
              >
                <SwiperSlide>
                  <Image
                    src={img1}
                    title="Premium Tapioca Starch - PP/PE Bags 850 Kg"
                    alt="Premium Tapioca Starch - PP/PE Bags 850 Kg"
                    width={678}
                    height={442}
                    className="img-fluid"
                    data-cms-key="ourproducts.poonphol.img1"
                  />
                </SwiperSlide>
                <SwiperSlide>
                  <Image
                    src={img2}
                    title="Premium Tapioca Starch - PP/PE Bags 50 Kg"
                    alt="Premium Tapioca Starch - PP/PE Bags 50 Kg"
                    width={678}
                    height={442}
                    className="img-fluid"
                    data-cms-key="ourproducts.poonphol.img2"
                  />
                </SwiperSlide>
                <SwiperSlide>
                  <Image
                    src={img3}
                    title="Premium Tapioca Starch - PP/PE Bags 25 Kg"
                    alt="Premium Tapioca Starch - PP/PE Bags 25 Kg"
                    width={678}
                    height={442}
                    className="img-fluid"
                    data-cms-key="ourproducts.poonphol.img3"
                  />
                </SwiperSlide>
              </Swiper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}